import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /** @type {MutationObserver | null} */
  #submenuMutationObserver = null;

  /** @type {number | null} */
  #deactivateTimeout = null;

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerenter', this.#handleOverflowPointerEnter);
    this.overflowMenu?.addEventListener('pointerleave', this.#handleOverflowPointerLeave);
    this.#cacheMaxOverflowMenuHeight();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.overflowMenu?.removeEventListener('pointerenter', this.#handleOverflowPointerEnter);
    this.overflowMenu?.removeEventListener('pointerleave', this.#handleOverflowPointerLeave);
    this.#cleanupMutationObserver();
    this.#clearDeactivateTimeout();
  }

  #handleOverflowPointerEnter = () => {
    this.#clearDeactivateTimeout();
  };

  #handleOverflowPointerLeave = (event) => {
    this.deactivate(event);
  };

  #clearDeactivateTimeout() {
    if (this.#deactivateTimeout !== null) {
      clearTimeout(this.#deactivateTimeout);
      this.#deactivateTimeout = null;
    }
  }

  #resizeListener = debounce(() => {
    this.#cacheMaxOverflowMenuHeight();
    setHeaderMenuStyle();
  }, 100);

  /** @type {State} */
  #state = {
    activeItem: null,
  };

  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  activate = (event) => {
    this.#clearDeactivateTimeout();
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);
    if (!item || item === this.#state.activeItem) return;

    const trigger = event.target.closest('[slot="more"], [slot="overflow"], .menu-list__list-item');
    const isOverflowTrigger = trigger instanceof Element && trigger.matches('[slot="more"], [slot="overflow"]');

    this.dataset.overflowExpanded = isOverflowTrigger.toString();

    const previouslyActiveItem = this.#state.activeItem;
    if (previouslyActiveItem) previouslyActiveItem.ariaExpanded = 'false';

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    if (!submenu && isOverflowTrigger) submenu = this.overflowMenu;

    if (submenu) {
      submenu.dataset.active = '';
      this.#cleanupMutationObserver();

      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      setTimeout(() => this.#cleanupMutationObserver(), 500);
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;
    this.headerComponent?.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const relatedTarget = event.relatedTarget instanceof Node ? event.relatedTarget : null;

    const isMovingToSubmenu = relatedTarget ? menu?.contains(relatedTarget) : false;
    const isMovingWithinMenu = relatedTarget ? this.contains(relatedTarget) : false;
    const isMovingToOverflowItem =
      relatedTarget instanceof Element &&
      (relatedTarget.matches('[slot="overflow"], [slot="more"]') ||
        relatedTarget.closest('[slot="overflow"], [slot="more"]') !== null);
    const isMovingToOverflowHost = relatedTarget === this.refs.overflowMenu;

    if (
      isMovingToSubmenu ||
      isMovingWithinMenu ||
      isMovingToOverflowItem ||
      isMovingToOverflowHost ||
      this.overflowHovered
    ) {
      this.#clearDeactivateTimeout();
      return;
    }

    this.#clearDeactivateTimeout();
    this.#deactivateTimeout = window.setTimeout(() => {
      this.#deactivateTimeout = null;
      const activeMenu = findSubmenu(this.#state.activeItem);
      if (activeMenu?.matches(':hover') || this.matches(':hover') || this.overflowHovered) return;
      this.#deactivate();
    }, 300);
  }

  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item !== this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.#clearDeactivateTimeout();
    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);
    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    if (submenu) delete submenu.dataset.active;
  };

  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cacheMaxOverflowMenuHeight() {
    const submenus = this.querySelectorAll('[ref="submenu[]"]');
    const heights = Array.from(submenus)
      .filter((submenu) => submenu instanceof HTMLElement)
      .map((submenu) => submenu.offsetHeight);
    const maxHeight = heights.length ? Math.max(...heights) : 0;
    this.headerComponent?.style.setProperty('--submenu-max-height', `${maxHeight}px`);
  }

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const container = element.closest('[slot="more"], [slot="overflow"], .menu-list__list-item') ?? element;

  if (container.matches('[slot="more"]')) {
    const firstOverflowItem = container.parentElement?.querySelector('[slot="overflow"]');
    return firstOverflowItem instanceof Element ? findMenuItem(firstOverflowItem) : null;
  }

  return /** @type {HTMLElement | null} */ (container.querySelector('[ref="menuitem"]'));
}

function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
